/**
 * Frontend OpenTelemetry tracing setup.
 *
 * Initializes a WebTracerProvider with:
 *   - Resource: service.name=clawfarm-frontend
 *   - Exporter: OTLP HTTP, same-origin /v1/traces (proxied by nginx to the
 *               otel-collector). Override with
 *               VITE_OTEL_EXPORTER_OTLP_ENDPOINT.
 *   - Sampler: ParentBased(TraceIdRatioBased(ratio)). ratio reads from
 *              VITE_OTEL_TRACES_SAMPLER_ARG (default 1.0).
 *   - Instrumentation: FetchInstrumentation so outbound `fetch()` calls to
 *                      the backend attach a W3C `traceparent` header.
 *                      CORS origins for trace header propagation are
 *                      configurable via VITE_OTEL_TRACE_ORIGINS (comma-
 *                      separated regex strings); defaults to localhost dev
 *                      ports + production clawfarm.ca.
 *
 * The setup is idempotent: calling `initTracing()` more than once is a no-op.
 *
 * Task 4 of the OTel Phase 1 plan.
 *
 * --- Task 19: prod disable toggle ----------------------------------------
 * Prod builds set `VITE_OTEL_DISABLED=true`. When set, `initTracing()` is a
 * complete no-op — no provider, exporter, or instrumentation is constructed.
 * This prevents double instrumentation: in prod, Azure App Service auto-injects
 * the Application Insights JS SDK, which handles client-side telemetry. Running
 * both the OTel Web SDK AND App Insights JS would duplicate traces, spans, and
 * error reports. The deploy pipeline sets `VITE_OTEL_DISABLED=true` at build
 * time; no `.env.production` file is needed.
 */

import { trace } from '@opentelemetry/api';
import {
  WebTracerProvider,
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
} from '@opentelemetry/sdk-trace-web';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';

/** Module-level guard against double-init. */
let initialized = false;

/**
 * Default trace propagation origins. Matches:
 *   - localhost on common dev ports (8080 = nginx, 5173 = vite, 3000 = alt)
 *   - clawfarm.ca (production)
 *
 * Staging (`clawfarm-staging.azurewebsites.net`) is intentionally NOT in the
 * default — operators must set VITE_OTEL_TRACE_ORIGINS explicitly so that
 * trace propagation is a conscious per-environment decision, not a silent
 * default that may not match the actual deploy target.
 */
const DEFAULT_TRACE_ORIGINS = ['localhost:(8080|5173|3000)', 'clawfarm\\.ca'];

/**
 * Parse a comma-separated list of regex strings into RegExp objects.
 *
 * Used for VITE_OTEL_TRACE_ORIGINS. Each segment is treated as a regex body
 * (without surrounding slashes). Empty/whitespace-only segments are skipped.
 *
 * Falls back to DEFAULT_TRACE_ORIGINS when `raw` is undefined or empty.
 *
 * @example
 *   parseTraceOrigins('localhost:8080,clawfarm\\.ca,clawfarm-staging\\.azurewebsites\\.net')
 *   // → [/localhost:8080/, /clawfarm\.ca/, /clawfarm-staging\.azurewebsites\.net/]
 */
function parseTraceOrigins(raw: string | undefined | null): RegExp[] {
  if (!raw || !raw.trim()) {
    return DEFAULT_TRACE_ORIGINS.map((s) => new RegExp(s));
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => new RegExp(s));
}

/**
 * Initialize the frontend tracer provider, OTLP exporter, and FetchInstrumentation.
 *
 * Safe to call multiple times — subsequent calls are no-ops.
 *
 * Prod builds set `VITE_OTEL_DISABLED=true` to skip OTel Web SDK init and avoid
 * double instrumentation with App Insights JS (auto-injected by App Service).
 */
export function initTracing(): void {
  // Prod toggle (Task 19): must be checked BEFORE the idempotency guard so
  // that a disabled call does NOT flip `initialized` to true. This keeps the
  // no-op contract clean: nothing happens, no state mutates.
  if (import.meta.env.VITE_OTEL_DISABLED === 'true') {
     
    console.info('[tracing] VITE_OTEL_DISABLED=true; skipping OTel Web SDK init (prod).');
    return;
  }

  if (initialized) {
    return;
  }

  const exporterUrl =
    (import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT as string | undefined) ?? '/v1/traces';

  const samplerRatio = Number(import.meta.env.VITE_OTEL_TRACES_SAMPLER_ARG ?? 1.0);

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: 'clawfarm-frontend',
  });

  const exporter = new OTLPTraceExporter({
    url: exporterUrl,
  });

  const provider = new WebTracerProvider({
    resource,
    sampler: new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(samplerRatio),
    }),
    spanProcessors: [new BatchSpanProcessor(exporter)],
  });

  provider.register();

  const fetchInstrumentation = new FetchInstrumentation({
    // Ensure the traceparent header is propagated to backend origins so the
    // backend can extract the W3C trace context and continue the trace.
    // Origins are configurable via VITE_OTEL_TRACE_ORIGINS (comma-separated
    // regex strings). Default covers localhost dev ports + production
    // clawfarm.ca. Staging and other environments must set the env var.
    propagateTraceHeaderCorsUrls: parseTraceOrigins(
      import.meta.env.VITE_OTEL_TRACE_ORIGINS,
    ),
  });

  registerInstrumentations({
    instrumentations: [fetchInstrumentation],
  });

  // Touch `trace` so the api package is loaded and the global provider
  // delegate resolves to our WebTracerProvider. (provider.register() already
  // does this; this is a belt-and-suspenders reference to avoid tree-shaking
  // removing the side effect in some build configurations.)
  void trace.getTracerProvider();

  initialized = true;
}
